import { useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { useSoftwareReleases } from './hooks/useSoftwareReleases';
import { Button } from '@/src/shadComponents/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';

interface ReleaseItem {
    title: string;
    description: string;
    bundleUri: string;
}

function ReleaseRow({ title, description, bundleUri }: ReleaseItem) {
    return (
        <div className="flex items-center justify-between gap-x-6 py-4">
            <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">{title}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                    {description}
                </p>
            </div>
            <Button variant="outline" size="sm" asChild>
                <a href={bundleUri}>
                    <Download className="size-4" />
                    Download
                </a>
            </Button>
        </div>
    );
}

export default function SoftwareReleases() {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [adapterRelease, setAdapterRelease] = useState<any>(null);
    const [macRelease, setMacRelease] = useState<any>(null);
    const [macSharingRelease, setMacSharingRelease] = useState<any>(null);
    const [macUploadingRelease, setMacUploadingRelease] = useState<any>(null);
    const [macProcessingRelease, setMacProcessingRelease] = useState<any>(null);
    const [romrAndroid, setRomrAndroid] = useState<any>(null);
    const { getLatestRelease } = useSoftwareReleases();
    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            try {
                const release = await getLatestRelease('smileport-adapter');
                const macRelease = await getLatestRelease('mac-client');
                const macProcessingUtility = await getLatestRelease(
                    'mac-processing-utility',
                );
                const macUploadingUtility = await getLatestRelease(
                    'mac-upload-utility',
                );
                const macSharingUtility = await getLatestRelease(
                    'mac-sharing-utility',
                );
                const romrAndroid = await getLatestRelease('romr-android');

                setRomrAndroid(romrAndroid);
                setAdapterRelease(release);
                setMacRelease(macRelease);
                setMacProcessingRelease(macProcessingUtility);
                setMacUploadingRelease(macUploadingUtility);
                setMacSharingRelease(macSharingUtility);
            } catch (e) {
                console.error(e);
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        load();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="space-y-6 px-6">
            <Card>
                <CardHeader>
                    <CardTitle>Smileport Utilities</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="divide-y divide-border">
                        <ReleaseRow
                            title="Smileport Adapter"
                            description="Utility application to bridge smileport devices to Photostat for sharing."
                            bundleUri={adapterRelease.bundleUri}
                        />
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Mac Utilities</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="divide-y divide-border">
                        <ReleaseRow
                            title="Mac Client"
                            description="Photostat client for Mac. Requires all of the following Mac utilities."
                            bundleUri={macRelease.bundleUri}
                        />
                        <ReleaseRow
                            title="Mac Upload Utility"
                            description="Utility application to upload photos to Photostat."
                            bundleUri={macUploadingRelease.bundleUri}
                        />
                        <ReleaseRow
                            title="Mac Processing Utility"
                            description="Utility application to process photos for sharing. Requires Imagemagick to be installed."
                            bundleUri={macProcessingRelease.bundleUri}
                        />
                        <ReleaseRow
                            title="Mac Sharing Utility"
                            description="Utility to allow the Photostat sharing station to connect to a Mac."
                            bundleUri={macSharingRelease.bundleUri}
                        />
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Android Utilities</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="divide-y divide-border">
                        <ReleaseRow
                            title="ROMR Android"
                            description="ROMR Android application."
                            bundleUri={romrAndroid.bundleUri}
                        />
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
